import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CampaignListTable from '../components/campaigns/CampaignListTable';
import { api } from '../lib/api';
import { Megaphone, Plus, ArrowLeft } from 'lucide-react';

const filterOptions = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'completed', label: 'Completed' },
];

const Campaigns = () => {
  const [searchParams] = useSearchParams();
  const patientId = searchParams.get('patient_id');
  const [patientName, setPatientName] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchPatientName = async () => {
      if (!patientId) return;
      try {
        const patient = await api.getPatient(patientId);
        if (patient?.name) {
          setPatientName(patient.name);
        }
      } catch (err) {
        console.error('Error fetching patient name:', err);
      }
    };
    fetchPatientName();
  }, [patientId]);

  useEffect(() => {
    const fetchCampaigns = async () => {
      setLoading(true);
      try {
        const data = await api.getCampaigns();
        setCampaigns(data.campaigns || []);
      } catch (err) {
        console.error('Error fetching campaigns:', err);
        setCampaigns([]);
      }
      setLoading(false);
    };
    fetchCampaigns();
  }, []);

  const filteredCampaigns = filter === 'all'
    ? campaigns
    : campaigns.filter(c => c.status === filter);

  const filterCounts = {
    all: campaigns.length,
    draft: campaigns.filter(c => c.status === 'draft').length,
    active: campaigns.filter(c => c.status === 'active').length,
    paused: campaigns.filter(c => c.status === 'paused').length,
    completed: campaigns.filter(c => c.status === 'completed').length,
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Page Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          {patientId && patientName ? (
            <>
              <Link
                to={`/patients?highlight=${patientId}`}
                className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-1"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to {patientName}
              </Link>
              <h1 className="text-2xl font-semibold text-slate-900">
                Campaigns for {patientName}
              </h1>
            </>
          ) : (
            <>
              <h1 className="text-2xl font-semibold text-slate-900">Campaigns</h1>
              <p className="mt-1 text-sm text-slate-500">
                Create and manage patient outreach campaigns
              </p>
            </>
          )}
        </div>
        <Link to="/campaigns/new">
          <button className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
            <Plus className="h-4 w-4" />
            New Campaign
          </button>
        </Link>
      </div>

      {/* Filter Pills */}
      <div className="flex items-center gap-2 mb-6">
        {filterOptions.map((option) => (
          <button
            key={option.value}
            onClick={() => setFilter(option.value)}
            className={`
              px-3 py-1.5 text-sm font-medium rounded-lg transition-colors
              ${filter === option.value
                ? 'bg-slate-900 text-white'
                : 'bg-white text-slate-600 hover:bg-slate-100 border border-slate-200'
              }
            `}
          >
            {option.label}
            {filterCounts[option.value] > 0 && (
              <span className={`ml-1.5 ${filter === option.value ? 'text-slate-300' : 'text-slate-400'}`}>
                {filterCounts[option.value]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Content */}
      {loading ? (
        <LoadingSkeleton />
      ) : filteredCampaigns.length === 0 ? (
        <EmptyState filter={filter} />
      ) : (
        <CampaignListTable campaigns={filteredCampaigns} />
      )}
    </div>
  );
};

function LoadingSkeleton() {
  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="divide-y divide-slate-100">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="px-6 py-4 flex items-center gap-4 animate-pulse">
            <div className="flex-1 space-y-2">
              <div className="h-4 w-48 bg-slate-100 rounded" />
              <div className="h-3 w-32 bg-slate-100 rounded" />
            </div>
            <div className="h-6 w-16 bg-slate-100 rounded-full" />
            <div className="h-4 w-20 bg-slate-100 rounded" />
            <div className="h-4 w-24 bg-slate-100 rounded" />
          </div>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ filter }) {
  const isFiltered = filter !== 'all';

  return (
    <div className="bg-white rounded-xl border border-slate-200 py-16 px-6">
      <div className="max-w-sm mx-auto text-center">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mx-auto mb-4">
          <Megaphone className="h-6 w-6 text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-1">
          {isFiltered ? `No ${filter} campaigns` : 'No campaigns yet'}
        </h3>
        <p className="text-sm text-slate-500 mb-6">
          {isFiltered
            ? 'Try selecting a different filter to find campaigns.'
            : 'Get started by creating your first campaign to engage patients.'
          }
        </p>
        {!isFiltered && (
          <Link to="/campaigns/new">
            <button className="inline-flex items-center gap-2 px-4 py-2 bg-slate-900 text-white text-sm font-medium rounded-lg hover:bg-slate-800 transition-colors">
              <Plus className="h-4 w-4" />
              Create Campaign
            </button>
          </Link>
        )}
      </div>
    </div>
  );
}

export default Campaigns;
